import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { MarkdownComponent, MarkdownModule } from 'ngx-markdown';
import * as fromGenerated from '../../../../_generated';
import * as fromFeatureFlags from '../../../../feature-flags';
import { DocumentInChatMessage } from '../../conversation.page';
import {
  excludeImageDocuments,
  isImageDocumentExtension,
} from './message-documents.helpers';
import { MessageQuickActionsSmartComponent } from './quick-actions/message-quick-actions.smart.component';

gsap.registerPlugin(SplitText);

export interface FileClickedEvent {
  documentUuid: string;
}

export interface ImageInMessageClickedEvent {
  documentUuid: string;
  filename: string;
}

export type MessageDocuments = Pick<
  fromGenerated.DocumentView,
  'uuid' | 'filename' | 'extension' | 'deletedAt' | 'status'
> & {
  isLoading?: boolean;
  isError?: boolean;
  isDisabled?: boolean;
  isClickable?: boolean; // by default, all documents are clickable
  type?: 'file' | 'document';
};

@Component({
  selector: 'squadbox-messages-listing-message',
  imports: [
    MarkdownModule,
    fromSeriousSystem.FileChipComponent,
    fromFeatureFlags.FeatureFlagsDirective,
    MessageQuickActionsSmartComponent,
  ],
  template: `
    <!--                 -->
    <!-- Loading Message -->
    <!--                 -->
    @if (isLoading()) {
      <div
        class="rounded-2xl typo-p2 p-2 prose bg-primary-200/20 animate-pulse text-transparent"
      >
        {{ messageContent() }}
      </div>
      <!--              -->
      <!-- User Message -->
      <!--              -->
    } @else if (isFromUser()) {
      <div class="fade-in flex flex-col items-end">
        <div
          class="px-4 py-3 max-w-70 rounded-2xl typo-p2 p-2 bg-primary-500/10"
          [class.tablet-landscape:max-w-110]="
            isFromUser() && !selectedDocumentInChatMessage()?.documentUuid
          "
          [class.hidden]="!messageContent()"
        >
          <markdown [data]="messageContent()" />
        </div>
        <div
          [id]="'scroll-anchor-' + messageId()"
          style="height: 0; scroll-margin-top: 152px"
        ></div>
        @let messageDocs = messageDocuments();
        @if (messageDocs.length > 0) {
          <!-- User Message Documents -->
          <div class="flex flex-wrap place-content-end pt-1 gap-1">
            @for (document of messageDocs; track document.uuid) {
              <sd-file-chip
                [filename]="document.filename"
                [fileType]="getFileTypeIconFromExtension(document.extension)"
                [isSelected]="
                  selectedDocumentInChatMessage()?.documentUuid ===
                    document.uuid &&
                  selectedDocumentInChatMessage()?.chatMessageId === messageId()
                "
                [isDisabled]="
                  !!document.deletedAt || (document.isDisabled ?? false)
                "
                [isClickable]="document.isClickable ?? true"
                [isLoading]="document.isLoading ?? false"
                [isError]="
                  document.isError || document.status === 'failed' || false
                "
                (chipClicked)="handleFileChipClicked(document)"
              />
            }
          </div>
        }
      </div>
      <!--                 -->
      <!-- Chatbot Message -->
      <!--                 -->
    } @else if (!isFromUser()) {
      <div class="flex gap-1 tablet-landscape:gap-3">
        <!-- Chatbot Message Content -->
        <div class="flex flex-col gap-2 typo-p2 p-2 max-w-full">
          <markdown
            [class]="{
              'fade-in': !isNewReply(),
              'opacity-0': isNewReply(),
            }"
            #chatbotMarkdown
            [data]="messageContent()"
          />
          <!-- Chatbot Message Quick Actions -->
          @let selectedChatId = chatId();
          @if (
            !isAssistantDescriptionOrIntroMessage() &&
            !isCompliancePhaseRegular() &&
            isQuickActionsDisplayed() &&
            ffQuickActions().hasAnyTrueValue &&
            selectedChatId
          ) {
            <squadbox-message-quick-actions-smart
              class="fade-in py-1"
              #chatbotQuickActions
              [chatId]="selectedChatId"
              [messageContent]="messageContent()"
              [messageId]="messageId()"
              [isProcessing]="isProcessing()"
              [isLastResponse]="isLastResponse()"
            />
          }
          @let chatbotReplyDocs = chatbotReplyDocuments();
          @if (chatbotReplyDocs.length > 0) {
            <div
              #chatbotDocumentsContainer
              [squadboxFeatureFlag]="'ff-display-document-sources'"
              class="fade-in flex flex-wrap pt-0 gap-2"
            >
              @for (document of chatbotReplyDocs; track document.uuid) {
                <sd-file-chip
                  [filename]="document.filename"
                  [fileType]="getFileTypeIconFromExtension(document.extension)"
                  [isLoading]="false"
                  [isDisabled]="!!document.deletedAt"
                  [isSelected]="
                    selectedDocumentInChatMessage()?.documentUuid ===
                      document.uuid &&
                    selectedDocumentInChatMessage()?.chatMessageId ===
                      messageId()
                  "
                  (chipClicked)="handleFileChipClicked(document)"
                />
              }
            </div>
          }
        </div>
      </div>
    }
  `,
})
export class MessagesListingMessageComponent implements OnDestroy {
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);

  private markdownBlocksAnimation: gsap.core.Timeline | null = null;
  public readonly chatbotMarkdown =
    viewChild<MarkdownComponent>('chatbotMarkdown');
  public readonly chatbotQuickActions =
    viewChild<MessageQuickActionsSmartComponent>('chatbotQuickActions');
  public readonly chatbotDocumentsContainer = viewChild<
    ElementRef<HTMLDivElement>
  >('chatbotDocumentsContainer');

  public readonly chatId = input<number | null>(null);
  public readonly messageId = input.required<number>();
  public readonly messageContent = input.required<string>();
  public readonly messageDocuments = input<MessageDocuments[]>([]);
  public readonly isFromUser = input.required<boolean>();
  public readonly isProcessing = input<boolean>(false);
  public readonly isNewReply = input<boolean>(false);
  public readonly isLoading = input<boolean>(false);
  public readonly isMobile = input<boolean>(false);
  public readonly isQuickActionsDisplayed = input<boolean>(false);
  public readonly isLastResponse = input<boolean>(false);
  public readonly isAssistantDescriptionOrIntroMessage = input<boolean>(false);
  public readonly isCompliancePhaseRegular = input<boolean>(false);
  public readonly selectedDocumentInChatMessage =
    input<DocumentInChatMessage | null>(null);
  public readonly ffDocumentViewer = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
      'ff-document-viewer',
    ),
  );
  public readonly documentClicked = output<FileClickedEvent>();
  public readonly imageClicked = output<ImageInMessageClickedEvent>();
  public readonly markdownAnimationComplete = output<void>();
  public readonly spacerShouldRecalculate = output<void>();
  public readonly ffQuickActions = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectQuickActions,
  );

  constructor() {
    effect(() => {
      const markdownEl = this.chatbotMarkdown();
      const content = this.messageContent();
      const fromUser = this.isFromUser();
      const isProcessing = this.isProcessing();
      const isNewReply = this.isNewReply();

      if (markdownEl && !fromUser && content && isNewReply) {
        this.animateMarkdownBlocks(
          markdownEl.element.nativeElement,
          () => this.markdownAnimationComplete.emit(),
          () => this.spacerShouldRecalculate.emit(),
        );
      } else if (!isProcessing) {
        this.cleanupResponseAnimation();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupResponseAnimation();
  }

  public getFileTypeIconFromExtension(extension: string) {
    return fromSeriousSystem.getFileTypeIconFromExtension(extension);
  }

  /**
   * Documents rendered as chips UNDER a chatbot reply (the
   * `ff-display-document-sources` row) — images excluded (see
   * `excludeImageDocuments`). Scoped to the chatbot block only; the
   * user-message chip row keeps `messageDocuments()` so the sender's own
   * attached-image chip still renders and opens the overlay.
   */
  public readonly chatbotReplyDocuments = computed(() =>
    excludeImageDocuments(this.messageDocuments()),
  );

  /**
   * Route an image chip to the overlay viewer, a document chip to
   * the PDF viewer. The image path is intentionally NOT gated by
   * `ff-document-viewer` — that flag governs the PDF viewer only; the image
   * overlay is its own feature. The document path keeps the existing gate.
   */
  public handleFileChipClicked(document: MessageDocuments) {
    if (isImageDocumentExtension(document.extension)) {
      this.imageClicked.emit({
        documentUuid: document.uuid,
        filename: document.filename,
      });
    } else if (this.ffDocumentViewer()) {
      this.documentClicked.emit({ documentUuid: document.uuid });
    }
  }

  private animateMarkdownBlocks(
    root: HTMLElement,
    onComplete?: () => void,
    onSpacerUpdate?: () => void,
  ) {
    /**
     * A CSS selector string that targets the blocks that should be animated.
     */
    const blockSelector = `p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, code, table, hr`;
    /**
     * An array of tag names that are explicitly marked for plain reveal.
     * These are typically elements that should not have word-by-word animation applied.
     */
    const plainRevealTags = new Set(['pre', 'code', 'table', 'hr']);
    /**
     * Specifies the easing function to be used for animations.
     */
    const ease: gsap.EaseString = 'power1.out';
    /**
     * A configuration property that determines whether the "Word Reveal" feature is enabled.
     * When set to true, the feature is activated.
     */
    const enableWordReveal = true;
    /**
     * The `blockOverlap` property defines the percentage value
     * to specify the Overlap from prev block sub-timeline start (e.g., '50%', '2 s')
     * 100% means no overlap
     */
    const blockOverlap = '100%';

    /**
     * Duration, in seconds, for the block reveal animation.
     * This setting applies to plain reveal blocks and blocks
     * that contain browser-generated elements (e.g., `::marker` in `<li>`).
     */
    const blockRevealDuration = 0.1;
    /**
     * Represents the duration (in seconds) for a word reveal animation.
     */
    const wordRevealDuration = 0.3;
    /**
     * Defines the duration (in seconds) for the reveal animation of quick actions.
     */
    const quickActionsRevealDuration = 0.2;
    /**
     * The duration in seconds for the reveal animation of documents.
     */
    const documentsRevealDuration = 0.2;
    /**
     * Represents the stagger delay between words in an animation or transition sequence.
     * The value is a numeric representation of time, often in seconds.
     */
    const wordStagger = 0.025;

    setTimeout(() => {
      const allBlocks = gsap.utils.selector(root)<HTMLElement>(blockSelector);

      // Only keep top-level blocks (exclude blocks that have another block ancestor).
      // This prevents cases like <li><p>...</p></li> where <p> would be hidden
      // while we animate its words.
      const blocks = allBlocks.filter((el) => {
        let parent = el.parentElement;
        while (parent && parent !== root) {
          if (allBlocks.includes(parent)) return false; // skip nested elements
          parent = parent.parentElement;
        }
        return true; // no block ancestor found => keep
      });
      if (!blocks.length) {
        onComplete?.();
        return;
      }

      const quickActions = this.chatbotQuickActions()?.elementRef
        .nativeElement as HTMLElement;
      const documentsContainer = this.chatbotDocumentsContainer()
        ?.nativeElement as HTMLElement;
      const master = gsap.timeline({
        defaults: { ease: ease },
        onComplete: () => onComplete?.(),
        onUpdate: () => onSpacerUpdate?.(),
      });

      // Keep everything hidden until it's that block's turn
      master.set(root, { autoAlpha: 0 });
      // Use display:none (not autoAlpha:0) so hidden blocks don't occupy layout space
      master.set(blocks, { display: 'none' });
      if (quickActions) {
        master.set(quickActions, { autoAlpha: 0 });
      }
      if (documentsContainer) {
        master.set(documentsContainer, { autoAlpha: 0 });
      }

      // Start with the root element and reveal it
      master.to(root, { autoAlpha: 1 });
      // Then, reveal each block in turn
      blocks.forEach((block, index) => {
        // For each block, create a sub-timeline to have more control over sequencing
        const sub = gsap.timeline();
        const tag = block.tagName.toLowerCase();
        const isPlainReveal = plainRevealTags.has(tag);

        // For complex blocks or when word reveal is disabled, just reveal them
        if (isPlainReveal || !enableWordReveal) {
          sub.set(block, { clearProps: 'display', autoAlpha: 0 });
          sub.to(block, {
            autoAlpha: 1,
            duration: blockRevealDuration,
          });
        } else {
          const delay = index === 0 ? 0 : -wordRevealDuration;
          sub.delay(delay);

          const split = new SplitText(block, { type: 'words' });
          const words = split.words ?? [];

          // Restore natural display (remove display:none) while keeping it invisible, then reveal words
          sub.set(block, { clearProps: 'display', autoAlpha: 0 });
          sub.set(words, { autoAlpha: 0 });
          sub.to(block, {
            autoAlpha: 1,
            duration: blockRevealDuration,
          });
          sub.to(words, {
            autoAlpha: 1,
            duration: wordRevealDuration,
            stagger: wordStagger,
            ease: ease,
            onComplete: () => split.revert(),
          });
        }

        // Add a sub timeline with overlap relative to the start of the previous one.
        // The first one starts at 0, the next ones start earlier by blockOverlap.
        const position = index === 0 ? '' : `<${blockOverlap}`;
        master.add(sub, position);
      });

      // Reveal quick actions and documents after the blocks
      if (quickActions) {
        master.to(quickActions, {
          autoAlpha: 1,
          duration: quickActionsRevealDuration,
        });
      }
      if (documentsContainer) {
        master.to(documentsContainer, {
          autoAlpha: 1,
          duration: documentsRevealDuration,
        });
      }

      this.markdownBlocksAnimation = master;
    }, 0); // to ensure the Markdown element is rendered
  }

  private cleanupResponseAnimation(): void {
    if (this.markdownBlocksAnimation) {
      this.markdownBlocksAnimation.kill();
      this.markdownBlocksAnimation = null;
    }
  }
}
